/**
 * The airship's exterior (ADR 0033), lifted from brad-airship
 * `airship-exterior.js` (the exterior pass of 27 September 2026 and the GPU
 * pass of 28 September the designer kept): the mooring island with its jetty
 * dressing and cliff shelf, and the ship: a lofted hull in strakes with
 * wales, a flat transom with the stern lights, the deck planking cut round
 * the companionways, the raised forecastle, a balustrade on the bulwarks, a
 * gaff-schooner rig (masts, shrouds as painted ratline cards, stays, booms,
 * gaffs, the set and furled sails, the fore topsail and jibs), flags, the
 * crew's laundry, the windlass and grapnel, the galley stovepipe, the rudder,
 * the lift stones in their cradles, the water casks, the skylight, the
 * companionways, pin rails, the sky skiff on its davits, rail lanterns, the
 * gangplank furniture and the mooring lines. Every object has a job, every
 * load a path (`docs/EXTERIOR-PASS-BRIEF.md`).
 *
 * Authored geometry only, in three.js's axes in the prototype's coordinates
 * (x toward the bow, y up, z south); the walking surfaces, colliders and
 * portals it dresses come from the scene record through `ShipArea`
 * (`area.ts`). Changed from the prototype: the materials live in the ship kit
 * (`kit.m`) and take their shader additions as hooks (`hooks.ts`); the
 * mooring post's rings and the deck cargo moved to their own prefabs
 * (`props.ts`), placed by the record's placements; the content ids use
 * underscores (`cliff_threshold`, `ship_deck`, `jetty_cargo`, ...); the
 * bridge is the room of type `cockpit`.
 */
import * as THREE from 'three';
import { addShaderHook } from '../material-hooks.ts';
import type { AreaSurface, ShipArea } from './area.ts';
import { type WindMode, grimeHook, planarHook, windAttribute, windHook } from './hooks.ts';
import { type ShipKit, type Vec3, at, beam, box, flags, last, primitive, tagMesh } from './kit.ts';

type V3 = [number, number, number];

const clamp = THREE.MathUtils.clamp;
const smooth = (a: number, b: number, x: number): number => THREE.MathUtils.smoothstep(x, a, b);
const lerp = THREE.MathUtils.lerp;

// ---------------------------------------------------------------------------
// Hull lines. +x is the bow. The deck walk rectangle (x 29..53, |z| ≤ 8) sits
// inside a lofted hull: flat transom aft, parallel midbody, fine entrance to a
// raked stem. Sections are superellipses, full amidships and finer forward.
export const HULL = { stern: 28.3, bow: 53, stem: 63.2, beam: 8.4, depth: 6 } as const;

export const halfBreadth = (x: number): number => {
  if (x <= HULL.bow) return lerp(8.2, HULL.beam, smooth(HULL.stern, 31.5, x));
  const t = clamp((x - HULL.bow) / (HULL.stem - HULL.bow), 0, 1);
  return HULL.beam * Math.pow(Math.max(0, 1 - Math.pow(t, 1.55)), 1.05);
};

export const keelDepth = (x: number): number => {
  if (x < HULL.bow + 1) return lerp(5.1, HULL.depth, smooth(HULL.stern, 35, x));
  const t = clamp((x - HULL.bow - 1) / (HULL.stem - HULL.bow - 0.2), 0, 1);
  return Math.max(1.9, HULL.depth * Math.sqrt(1 - t * t * 0.93));
};

// Sheer: top of the solid bulwark, rising toward both ends. Low waist
// amidships (open balustrade), high solid bulwarks at bow and stern. A raised
// forecastle (deck at FOC) forward of the bridge gives the bow its step; it is
// working space for the windlass and anchor, not walkable.
export const FOC = 1.0;
export const FOC_X = 53.1;
export const FOC_NOSE = 54.05;
export const sheer = (x: number): number => 0.45 + 0.62 * Math.pow(1 - smooth(HULL.stern, 32.5, x), 1.3) + FOC * smooth(52.2, 54.2, x) + 0.75 * Math.pow(smooth(50, HULL.stem, x), 1.5);

/** Rail cap height: a balustrade on the low waist, the bulwark's own cap where it is already chest high. */
export const railCap = (x: number): number => (x > FOC_X + 0.3 ? sheer(x) + 0.6 : Math.max(sheer(x) + 0.2, 1.05));

// Sections: V-shaped in the run aft (fine lines to the rudder), full and a
// little bulged below the deck amidships, sharp in the entrance forward.
const fullness = (x: number): number => (x < HULL.bow ? lerp(1.3, 2.6, smooth(HULL.stern, 37, x)) : lerp(2.6, 1.55, smooth(HULL.bow, HULL.stem, x)));

export const hullZ = (x: number, y: number): number => {
  const B = halfBreadth(x);
  const D = keelDepth(x);
  if (y >= 0) return Math.max(0, B - 0.035 * y);
  const s = Math.min(1, -y / D);
  const n = fullness(x);
  const bulge = 1 + 0.075 * Math.sin(Math.PI * Math.min(1, s * 1.7));
  return B * bulge * Math.pow(Math.max(0, 1 - Math.pow(s, n)), 1 / n);
};

const hullStations = (): number[] => {
  const xs: number[] = [];
  for (let x = HULL.stern; x < HULL.bow - 0.01; x += 0.8) xs.push(x);
  for (let k = 0; k <= 22; k += 1) {
    const t = k / 22;
    xs.push(HULL.bow + (HULL.stem - HULL.bow) * (1 - Math.pow(1 - t, 1.35)));
  }
  return xs;
};

/**
 * One horizontal band of hull skin between absolute heights yTop and yBottom,
 * both sides. UVs are metres/4 along the hull and along the girth, so the
 * plank texture reads as strakes running bow to stern.
 */
const hullBand = (yTop: number, yBottom: number, rows: number, followSheer = false): THREE.BufferGeometry => {
  const xs = hullStations();
  const pos: number[] = [];
  const uv: number[] = [];
  const index: number[] = [];
  for (const side of [1, -1]) {
    const base = pos.length / 3;
    for (const x of xs) {
      const top = followSheer ? sheer(x) : yTop;
      const D = keelDepth(x);
      let girth = 0;
      let prev: V3 | null = null;
      for (let j = 0; j <= rows; j += 1) {
        let y = lerp(top, yBottom, j / rows);
        y = Math.max(y, -D);
        const z = hullZ(x, y) * side;
        const p: V3 = [x, y, z];
        if (prev !== null) girth += Math.hypot(p[1] - prev[1], p[2] - prev[2]);
        prev = p;
        pos.push(...p);
        uv.push(x / 4, (girth + (followSheer ? 0 : -yTop)) / 4);
      }
    }
    const stride = rows + 1;
    for (let i = 0; i < xs.length - 1; i += 1) {
      for (let j = 0; j < rows; j += 1) {
        const a = base + i * stride + j;
        const b = a + stride;
        const c = a + 1;
        const d = b + 1;
        if (side > 0) index.push(a, c, b, b, c, d);
        else index.push(a, b, c, b, d, c);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(index);
  g.computeVertexNormals();
  return g;
};

const catenary = (a: Vec3, b: Vec3, sag: number, n = 10): V3[] => {
  const out: V3[] = [];
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    out.push([lerp(a[0], b[0], t), lerp(a[1], b[1], t) - sag * 4 * t * (1 - t), lerp(a[2], b[2], t)]);
  }
  return out;
};

type MakeOptions = {
  readonly planar?: readonly [THREE.Texture, number];
  readonly uv?: THREE.Texture;
  readonly wind?: WindMode;
  readonly rough?: number;
  readonly metal?: number;
  readonly side?: THREE.Side;
  readonly emissive?: string;
  readonly glow?: number;
  readonly noShadow?: boolean;
  /** A texture multiplied in after the planar map (the hull's grime). */
  readonly grime?: THREE.Texture;
};

/**
 * The exterior's materials, into the kit (`kit.m`) under the prototype's keys. Tint-only variants share one
 * vertex-coloured base per texture (one draw each): the world bakes the variant's colour into the batch. Idempotent.
 */
export const exteriorMaterials = (kit: ShipKit): void => {
  const m = kit.m;
  if (m['hullWood'] !== undefined) return;
  const tex = kit.textures();
  const make = (key: string, color: string, o: MakeOptions = {}): THREE.MeshStandardMaterial => {
    const mat = new THREE.MeshStandardMaterial({ color, roughness: o.rough ?? 0.92, metalness: o.metal ?? 0 });
    if (o.side !== undefined) mat.side = o.side;
    if (o.emissive !== undefined) {
      mat.emissive.set(o.emissive);
      mat.emissiveIntensity = o.glow ?? 0;
    }
    if (o.uv !== undefined) mat.map = o.uv;
    if (o.planar !== undefined) addShaderHook(mat, planarHook(o.planar[0], o.planar[1]));
    if (o.wind !== undefined) addShaderHook(mat, windHook(kit.wind, o.wind));
    if (o.grime !== undefined) addShaderHook(mat, grimeHook(o.grime));
    if (o.noShadow === true) flags(mat).noShadow = true;
    kit.finish(mat);
    mat.name = `airship-${key}`;
    m[key] = mat;
    return mat;
  };
  make('deck', '#b99466', { planar: [tex.deck, 0.25] });
  const base = (key: string, o: MakeOptions, variants: Readonly<Record<string, string>>, fill?: readonly (readonly [string, number])[]): THREE.MeshStandardMaterial => {
    const b = make(key, '#ffffff', o);
    b.vertexColors = true;
    if (fill !== undefined) flags(b).fillAttributes = fill;
    for (const [k, color] of Object.entries(variants)) {
      const v = new THREE.MeshStandardMaterial({ color });
      flags(v).batchAs = b;
      v.name = `airship-${k}`;
      m[k] = v;
    }
    return b;
  };
  {
    // The hull's grime: drips under each lower-deck light and scupper, rust under the chainplates, bloom at the lift cradles.
    const windows: number[] = [];
    for (let x = 31.2; x < 51; x += 2.45) windows.push(x);
    const scuppers: number[] = [];
    for (let x = 30.5; x < 53; x += 3.1) scuppers.push(x);
    const chainplates: number[] = [];
    for (const mx of [36, 43.5]) for (const dx of [-1.1, 0, 1.1]) chainplates.push(mx + dx);
    const grime = tex.grime({ windows, chainplates, cradles: [33.2, 40.8, 48.4], scuppers });
    base('hullBase', { uv: tex.hull, rough: 0.85, side: THREE.DoubleSide, grime }, { hullWood: '#8a6040', hullPaint: '#40657d', hullTar: '#4a3a2e', bulwark: '#d9c9a0' });
  }
  base('timberBase', { planar: [tex.timber, 0.45] }, { wale: '#3a2a20', rail: '#6b4a30', spar: '#9a7248' });
  // Balusters, small ironwork and lantern cages: shadow detail.
  flags(base('paintBase', { planar: [tex.paint, 0.5], rough: 0.8 }, { baluster: '#e4d7b3' })).detailShadow = true;
  flags(base('metalBase', { planar: [tex.iron, 1], rough: 0.55, metal: 0.5 }, { iron: '#34322f', brass: '#b08a48' })).detailShadow = true;
  base('ropeBase', { wind: 'rope', noShadow: true }, { rope: '#b59d74', ropeTaut: '#a38b62' }, [['aWind', 4]]);
  make('mainsail', '#f1e8d2', { uv: tex.sail(true), wind: 'sail', side: THREE.DoubleSide, rough: 0.95 });
  make('foresail', '#efe5cc', { uv: tex.sail(false), wind: 'sail', side: THREE.DoubleSide, rough: 0.95 });
  make('furl', '#e7dcc0', { uv: tex.canvas, rough: 0.95 });
  base('laundryBase', { wind: 'cloth', side: THREE.DoubleSide, rough: 0.95, noShadow: true }, { laundryWhite: '#e9e3d3', laundryBlue: '#4d6f8e', laundryRed: '#b8432f', laundryTan: '#c9a67a' });
  base('flagBase', { wind: 'flag', side: THREE.DoubleSide, rough: 0.9, noShadow: true }, { flag: '#b3392c', flagCream: '#ecdfbd' });
  flags(make('lanternIron', '#2d2b29', { wind: 'lantern', rough: 0.6, metal: 0.4 })).detailShadow = true;
  // Glass, lamps and lift crystals pass light: they cast no shadow (and cost no shadow-pass draw). So does the flat dark fill inside openings.
  flags(make('lampGlass', '#ffe2a6', { wind: 'lantern', emissive: '#ffb24d', glow: 0.08, rough: 0.3, noShadow: true })).nightGlow = 3.2;
  const win = make('windowGlass', '#2b3944', { emissive: '#ffb866', glow: 0, rough: 0.25, metal: 0.1, noShadow: true });
  flags(win).nightGlow = 1.9;
  flags(make('lampFixed', '#ffe6b0', { emissive: '#ffc062', glow: 0.06, rough: 0.3, noShadow: true })).nightGlow = 3;
  flags(make('liftStone', '#9ff0ff', { emissive: '#44d6ff', glow: 0.9, rough: 0.2, noShadow: true })).liftGlow = 0.9;
  make('prop', '#a47d4b', { wind: 'prop', planar: [tex.timber, 0.5], rough: 0.7 });
  make('shade', '#07090b', { rough: 1, noShadow: true });
  const rat = make('ratlineCard', '#b5a07a', { uv: tex.ratline(), side: THREE.DoubleSide, rough: 0.95, noShadow: true });
  rat.transparent = true;
  rat.depthWrite = false;
  rat.alphaTest = 0.02;
  make('islandRock', '#ffffff', { planar: [tex.rock, 0.18], rough: 1 }).vertexColors = true;
  make('islandGrass', '#ffffff', { planar: [tex.grass, 0.3], rough: 1 }).vertexColors = true;
  const leaf = make('foliage', '#ffffff', { planar: [tex.grass, 0.6], rough: 1 });
  leaf.vertexColors = true;
  leaf.flatShading = true;
  make('wingCanvas', '#e1d4b4', { uv: tex.canvas, side: THREE.DoubleSide, rough: 0.95 });
  // The jetty's weathered boards, and the cliff shelf's faceted stone (vertex-coloured).
  const alternate = kit.get('floor').clone();
  alternate.color.multiplyScalar(0.91);
  alternate.name = 'airship-deckAlternate';
  m['deckAlternate'] = kit.finish(alternate);
  const rock = kit.get('cliff').clone();
  rock.color.set('#ffffff');
  rock.vertexColors = true;
  rock.flatShading = true;
  rock.name = 'airship-mooringRock';
  m['mooringRock'] = kit.finish(rock);
};

/**
 * A hanging (or standing) ship's lantern: iron hood and frame round a glass chimney. Hung lanterns swing a little
 * from their hook in the wind.
 */
export const lantern = (root: THREE.Object3D, kit: ShipKit, [x, y, z]: Vec3, standing = false): void => {
  const hook: Vec3 = [x, y, z];
  const drop = standing ? 0 : 0.42;
  const part = (mat: string, g: THREE.BufferGeometry, px: number, py: number, pz: number): THREE.Mesh => {
    g.translate(px, py, pz);
    windAttribute(g, (v) => [standing ? 0 : Math.max(0, hook[1] - v.y) / 0.5, x + z, 0, 0]);
    return primitive(root, kit.get(mat), g);
  };
  const cy = standing ? y + 0.16 : y - drop + 0.02;
  if (!standing) part('lanternIron', new THREE.CylinderGeometry(0.012, 0.012, drop - 0.1, 3), x, y - (drop - 0.1) / 2, z);
  part('lanternIron', new THREE.CylinderGeometry(0.04, 0.13, 0.1, 6), x, cy + 0.17, z);
  part('lampGlass', new THREE.CylinderGeometry(0.09, 0.09, 0.2, 6), x, cy, z);
  part('lanternIron', new THREE.CylinderGeometry(0.12, 0.1, 0.05, 6), x, cy - 0.12, z);
  for (let k = 0; k < 3; k += 1) {
    const a = k * 2.09;
    part('lanternIron', new THREE.BoxGeometry(0.018, 0.24, 0.018), x + Math.cos(a) * 0.1, cy, z + Math.sin(a) * 0.1);
  }
};

// ---------------------------------------------------------------------------
// The mooring island: the jetty is cantilevered off a floating sky-island. A
// grassy top rising west, broken strata cliffs, and a long tapering root
// underneath; a few wind-bent trees and the dock lamp. Visual only: the walk
// polygon (the cliff threshold) and the jetty stay exactly as authored.
const buildIsland = (root: THREE.Object3D, kit: ShipKit): void => {
  let seed = 4242;
  const r = (): number => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const cx = -9;
  const cz = 0.5;
  const N = 72;
  const noise = (a: number, k: number): number => Math.sin(a * 3 + k) * 0.5 + Math.sin(a * 7.3 + k * 2.1) * 0.3 + Math.sin(a * 13.7 + k) * 0.2;
  const R = (a: number): number => 16.5 + 2.2 * noise(a, 1);
  const top = (x: number, z: number): number => {
    if (x > 4.3) return -0.35;
    const rise = Math.min(1, (4.3 - x) / 9);
    return 0.05 + rise * rise * 3.4 + 0.5 * Math.sin(x * 0.4 + z * 0.3) * rise + 0.25 * Math.sin(z * 0.9) * rise;
  };
  const rings: readonly (readonly [number, number, 'top' | 'lip' | 'wall' | 'root'])[] = [
    [1, 0, 'top'],
    [1.03, -1.4, 'lip'],
    [1.0, -4.2, 'wall'],
    [0.93, -7.5, 'wall'],
    [0.78, -12, 'root'],
    [0.58, -17.5, 'root'],
    [0.38, -23, 'root'],
    [0.2, -28, 'root'],
  ];
  const pts: V3[][] = rings.map(([s, y, kind], k) => {
    const out: V3[] = [];
    for (let i = 0; i < N; i += 1) {
      const a = (i / N) * Math.PI * 2;
      const rad = R(a) * s * (1 + (k !== 0 ? 0.06 * noise(a, k * 3.1) : 0));
      const x = cx + Math.cos(a) * rad;
      const z = cz + Math.sin(a) * rad * 0.95;
      out.push([x, kind === 'top' ? top(x, z) : y + (k > 1 ? noise(a, k) * 0.9 : 0) + (kind === 'lip' ? top(x, z) - 0.2 : 0), z]);
    }
    return out;
  });
  const rock: number[] = [];
  const rc: number[] = [];
  const grass: number[] = [];
  const gc: number[] = [];
  const stone = (y: number, f: number): number[] => {
    const t = clamp((y + 28) / 28, 0, 1);
    const c = new THREE.Color('#4a3f3a').lerp(new THREE.Color('#9c8466'), t * t).lerp(new THREE.Color('#6d6a52'), y > -1.8 ? 0.45 : 0);
    return c.multiplyScalar(0.8 + 0.3 * f).toArray();
  };
  const tri = (arr: number[], col: number[], a: Vec3, b: Vec3, c: Vec3, colour: (p: Vec3) => number[]): void => {
    arr.push(...a, ...b, ...c);
    for (const p of [a, b, c]) col.push(...colour(p));
  };
  for (let k = 0; k < pts.length - 1; k += 1) {
    for (let i = 0; i < N; i += 1) {
      const j = (i + 1) % N;
      const ring = at(pts, k);
      const next = at(pts, k + 1);
      const a = at(ring, i);
      const b = at(ring, j);
      const c = at(next, i);
      const d = at(next, j);
      const f = r();
      const col = (p: Vec3): number[] => stone(p[1], f);
      tri(rock, rc, a, c, b, col);
      tri(rock, rc, b, c, d, col);
    }
  }
  const tip: Vec3 = [cx + 1, -33, cz];
  const lowest = last(pts);
  for (let i = 0; i < N; i += 1) {
    const j = (i + 1) % N;
    const f = r();
    tri(rock, rc, at(lowest, i), tip, at(lowest, j), (p) => stone(p[1], f));
  }
  // Grass top: a polar grid so the ground can roll; darker in hollows.
  const rings2 = 10;
  const grid: V3[][] = [];
  const crown = at(pts, 0);
  for (let q = 0; q <= rings2; q += 1) {
    const row: V3[] = [];
    for (let i = 0; i < N; i += 1) {
      const a = (i / N) * Math.PI * 2;
      const t = q / rings2;
      const rad = R(a) * t;
      const x = cx + Math.cos(a) * rad;
      const z = cz + Math.sin(a) * rad * 0.95;
      row.push([x, q === rings2 ? at(crown, i)[1] : top(x, z), z]);
    }
    grid.push(row);
  }
  const green = (p: Vec3): number[] => {
    const c = new THREE.Color('#5f7440')
      .lerp(new THREE.Color('#9a9458'), clamp(p[1] / 4, 0, 1) * 0.55)
      .lerp(new THREE.Color('#8c7a58'), clamp(1 - (p[1] + 0.2) / 0.8, 0, 1) * 0.5);
    return c.multiplyScalar(0.82 + 0.12 * Math.sin(p[0] * 1.3 + p[2] * 0.7)).toArray();
  };
  for (let q = 0; q < rings2; q += 1) {
    for (let i = 0; i < N; i += 1) {
      const j = (i + 1) % N;
      const row = at(grid, q);
      const next = at(grid, q + 1);
      const a = at(row, i);
      const b = at(row, j);
      const c = at(next, i);
      const d = at(next, j);
      tri(grass, gc, a, b, c, green);
      tri(grass, gc, b, d, c, green);
    }
  }
  const mesh = (arr: number[], col: number[], mat: string): void => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((arr.length / 3) * 2), 2));
    g.computeVertexNormals();
    primitive(root, kit.get(mat), g);
  };
  mesh(rock, rc, 'islandRock');
  mesh(grass, gc, 'islandGrass');
  // Wind-bent trees on the rise (the trunk leans downwind, crowns in clumps).
  for (const [x, z, h] of [
    [-14, -7, 8],
    [-11, 6.5, 6.8],
    [-6, -10.5, 6],
    [-18, 1.5, 9],
    [-3.5, 9, 5.2],
    [-8.5, -2, 7.2],
    [-20, -6, 7.5],
    [-16, 9, 6.4],
  ] as const) {
    const y = top(x, z);
    beam(root, kit.get('wale'), [x, y - 0.3, z], [x + 0.5, y + h * 0.62, z + 0.2], 0.16 + h * 0.02, 6);
    const lp: number[] = [];
    const lc: number[] = [];
    for (const [dx, dy, dz, s] of [
      [0.5, h * 0.72, 0.2, 1.25],
      [-0.45, h * 0.62, 0.4, 0.95],
      [0.9, h * 0.58, -0.5, 0.9],
      [0.3, h * 0.95, 0, 0.85],
    ] as const) {
      // An icosahedron is already non-indexed (the prototype's `toNonIndexed` only warned).
      const ico = new THREE.IcosahedronGeometry(s * (h / 5), 0);
      const g = ico.getIndex() === null ? ico : ico.toNonIndexed();
      g.translate(x + dx, y + dy, z + dz);
      const p = g.getAttribute('position');
      for (let i = 0; i < p.count; i += 3) {
        const c = new THREE.Color('#4f6e34').lerp(new THREE.Color('#8ea24c'), clamp((p.getY(i) - y - dy + s) / (2 * s), 0, 1)).multiplyScalar(0.85 + 0.3 * r());
        for (let k = 0; k < 3; k += 1) {
          lp.push(p.getX(i + k), p.getY(i + k), p.getZ(i + k));
          lc.push(...c.toArray());
        }
      }
      g.dispose();
      if (g !== ico) ico.dispose();
    }
    mesh(lp, lc, 'foliage');
  }
  // Dock lamp at the head of the jetty, for coming aboard after dark.
  const lx = 7.25;
  const lz = -2.05;
  const iron = kit.get('iron');
  beam(root, iron, [lx, 0, lz], [lx, 2.6, lz], 0.06, 6);
  beam(root, iron, [lx, 2.55, lz], [lx + 0.45, 2.55, lz], 0.03, 4);
  box(root, iron, lx + 0.45, 2.47, lz, 0.2, 0.05, 0.2);
  box(root, kit.get('lampFixed'), lx + 0.45, 2.33, lz, 0.15, 0.22, 0.15);
  box(root, iron, lx + 0.45, 2.2, lz, 0.18, 0.04, 0.18);
  box(root, iron, lx, 0.08, lz, 0.3, 0.16, 0.3);
};

const surfaceById = (area: ShipArea, id: string): AreaSurface | undefined => area.surfaces.find((s) => s.id === id);

/** The cliff mooring (the Higuera coordinates), unchanged from the approved layout: the jetty's boards, fascia and corbels, and the faceted cliff shelf. */
const buildMooring = (root: THREE.Object3D, kit: ShipKit, area: ShipArea): void => {
  buildIsland(root, kit);
  kit.get('cliff').color.set('#7d6d5c');
  const B = (mat: string, x: number, y: number, z: number, w: number, h: number, d: number): THREE.Mesh => box(root, kit.get(mat), x, y, z, w, h, d);
  const line = (mat: string, a: Vec3, b: Vec3, r = 0.06, n = 6): THREE.Mesh => beam(root, kit.get(mat), a, b, r, n);
  // Mooring dressing is visual only: the walk polygon, boards, supports and post stay owned by the record.
  const mooring = surfaceById(area, 'mooring');
  const threshold = surfaceById(area, 'cliff_threshold');
  if (mooring === undefined || threshold === undefined) return;
  const polygon = threshold.polygon ?? [];
  const y = mooring.y;
  // Staggered plank ends and alternate weathered boards break the broad slab.
  for (let row = 0, z = mooring.z0 + 0.16; z < mooring.z1; row += 1, z += 0.32) {
    const cuts = [mooring.x0];
    for (let x = mooring.x0 + 1.65 + (row % 3) * 0.91; x < mooring.x1 - 0.25; x += 3.25) cuts.push(x);
    cuts.push(mooring.x1);
    for (let j = 0; j < cuts.length - 1; j += 1) {
      const a = at(cuts, j);
      const b = at(cuts, j + 1);
      const depth = Math.min(0.292, mooring.z1 - z + 0.15);
      B((row + j) % 4 === 0 ? 'deckAlternate' : 'floor', (a + b) / 2, y + 0.012, z, b - a - 0.026, 0.024, depth);
      for (const x of [a + 0.1, b - 0.1]) {
        for (const dz of [-0.09, 0.09]) {
          const pin = new THREE.PlaneGeometry(0.034, 0.035);
          pin.rotateX(-Math.PI / 2);
          primitive(root, kit.get('trim'), pin, x, y + 0.029, z + dz);
        }
      }
    }
  }
  // Three horizontal fascia courses and broad exposed corbels give the edge a structural scale; pinheads and narrow
  // seams provide the smaller scale.
  for (const z of [mooring.z0 - 0.055, mooring.z1 + 0.055]) {
    for (let course = 0; course < 3; course += 1) B(course === 1 ? 'floor' : 'timber', (mooring.x0 + mooring.x1) / 2, y - 0.15 - course * 0.24, z, mooring.x1 - mooring.x0 + 0.1, 0.21, 0.2);
    B('cream', (mooring.x0 + mooring.x1) / 2, y - 0.025, z, mooring.x1 - mooring.x0 + 0.15, 0.1, 0.22);
    for (let x = mooring.x0 + 0.32; x < mooring.x1; x += 2.15) {
      B('trim', x, y - 0.37, z + Math.sign(z - 0.5) * 0.12, 0.2, 0.66, 0.08);
      for (const yy of [-0.13, -0.57]) primitive(root, kit.get('warm'), new THREE.SphereGeometry(0.047, 5, 3), x, y + yy, z + Math.sign(z - 0.5) * 0.172);
      line('timber', [x, y - 0.18, z], [x, y - 1.0, z - Math.sign(z - 0.5) * 0.62], 0.12, 6);
    }
  }
  // The east apron stops outside the exact gangplank corridor; nothing crosses it.
  const gp = surfaceById(area, 'gangplank');
  for (const [a, b] of [
    [mooring.z0, (gp?.z0 ?? -0.0) - 0.18],
    [(gp?.z1 ?? 0) + 0.18, mooring.z1],
  ] as const) {
    if (b <= a) continue;
    B('timber', mooring.x1 + 0.035, y - 0.28, (a + b) / 2, 0.2, 0.55, b - a);
    B('cream', mooring.x1 + 0.035, y - 0.025, (a + b) / 2, 0.22, 0.1, b - a);
    for (let z = a + 0.28; z < b; z += 1.1) B('trim', mooring.x1 + 0.15, y - 0.24, z, 0.06, 0.39, 0.14);
  }
  // A closed, faceted cliff shelf encloses the former rectangular heel. The upper ring follows the authored
  // threshold, then widens only outside its west boundary; the walkable polygon itself stays as authored.
  const contour: [number, number][] = [[4.8, -4.3], [6.35, -3.95], ...polygon.map(([x, z]): [number, number] => [Math.min(x, mooring.x0 - 0.055), z]), [6.3, 4.0], [4.65, 4.4]];
  const n = contour.length;
  const center = [7.25, 0.05] as const;
  const layers = [0.026, -1.32, -3.08, -5.35, -7.9];
  const expands = [0, 0.24, 0.1, 0.35, -0.03];
  const shelf: V3[][] = layers.map((yy, k) =>
    contour.map(([x, z], i): V3 => {
      const expand = at(expands, k);
      const jitter = k === 0 ? 0 : Math.sin(i * 2.73 + k) * 0.13;
      return [x + (x < center[0] ? -expand : expand * 0.25) + jitter, yy + (k === 0 ? 0 : Math.sin(i * 1.8 + k) * 0.17), z + (z < 0 ? -expand : expand) + jitter];
    }),
  );
  const positions: number[] = [];
  const colors: number[] = [];
  // Authored stone-value field: warm limestone around the fixed arrival focal point (9, 0), cooling into shaded outer
  // ledges. Static material colour, not another light or random per-face noise.
  const outerStone = new THREE.Color('#655f54');
  const sunStone = new THREE.Color('#cbb58c');
  const wallStone = new THREE.Color('#49423c');
  const stoneColor = ([x, yy, z]: Vec3, tone: number): THREE.Color => {
    const pool = Math.exp(-1.65 * (((x - 9) / 2.15) ** 2 + ((z - 0.05) / 1.6) ** 2));
    const surface = outerStone.clone().lerp(sunStone, pool);
    const rim = clamp((yy + 0.38) / 0.405, 0, 1);
    return wallStone.clone().lerp(surface, rim).multiplyScalar(0.82 + tone * 0.18);
  };
  const tri = (a: Vec3, b: Vec3, c: Vec3, tone: number): void => {
    positions.push(...a, ...b, ...c);
    for (const point of [a, b, c]) colors.push(...stoneColor(point, tone).toArray());
  };
  const mid = (p: Vec3, q: Vec3): V3 => [(p[0] + q[0]) * 0.5, (p[1] + q[1]) * 0.5, (p[2] + q[2]) * 0.5];
  const topTri = (a: Vec3, b: Vec3, c: Vec3, tone: number, depth = 2): void => {
    if (depth === 0) {
      tri(a, b, c, tone);
      return;
    }
    const ab = mid(a, b);
    const bc = mid(b, c);
    const ca = mid(c, a);
    topTri(a, ab, ca, tone, depth - 1);
    topTri(ab, b, bc, tone, depth - 1);
    topTri(ca, bc, c, tone, depth - 1);
    topTri(ab, bc, ca, tone, depth - 1);
  };
  for (let k = 0; k < shelf.length - 1; k += 1) {
    for (let i = 0; i < n; i += 1) {
      const j = (i + 1) % n;
      const ring = at(shelf, k);
      const next = at(shelf, k + 1);
      const tone = 0.77 + ((i * 7 + k * 3) % 9) * 0.052;
      // The contour winds counter-clockwise in XZ; face normals point away from the centre.
      tri(at(ring, i), at(ring, j), at(next, i), tone);
      tri(at(ring, j), at(next, j), at(next, i), tone * 0.96);
    }
  }
  // Triangulate the concave shelf with restrained large tonal facets. The top sits above the retained pick plane,
  // with no coplanar overlay.
  const shape = new THREE.Shape(contour.map(([x, z]) => new THREE.Vector2(x, -z)));
  const topShape = new THREE.ShapeGeometry(shape);
  topShape.rotateX(-Math.PI / 2);
  const tp = topShape.getAttribute('position');
  const ix = topShape.getIndex();
  if (ix !== null) {
    for (let i = 0; i < ix.count; i += 3) {
      const p = (k: number): V3 => {
        const j = ix.getX(i + k);
        return [tp.getX(j), 0.027, tp.getZ(j)];
      };
      topTri(p(0), p(1), p(2), 1.02);
    }
  }
  topShape.dispose();
  // Close the underside with the same ring vertices (no overlapping skin).
  const bottomRing = last(shelf);
  const bottomFaces = THREE.ShapeUtils.triangulateShape(
    bottomRing.map(([x, , z]) => new THREE.Vector2(x, -z)),
    [],
  );
  for (const face of bottomFaces) tri(at(bottomRing, at(face, 2)), at(bottomRing, at(face, 1)), at(bottomRing, at(face, 0)), 0.73);
  // Worn polygonal flagstones form the fine scale on the traversable shelf: every stone inside the authored polygon,
  // only 4 cm above it.
  const inside = (x: number, z: number): boolean => {
    let yes = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
      const a = at(polygon, i);
      const b = at(polygon, j);
      if (a[1] > z !== b[1] > z && x < ((b[0] - a[0]) * (z - a[1])) / (b[1] - a[1]) + a[0]) yes = !yes;
    }
    return yes;
  };
  let stoneIndex = 0;
  for (let z = threshold.z0 + 0.3; z < threshold.z1 - 0.15; z += 0.61) {
    for (let x = threshold.x0 + 0.2; x < mooring.x0 - 0.2; x += 0.78) {
      const xx = x + Math.sin(stoneIndex * 2.1) * 0.06;
      const zz = z + Math.cos(stoneIndex * 1.7) * 0.06;
      stoneIndex += 1;
      const edge = (
        [
          [-0.32, -0.23],
          [0.22, -0.25],
          [0.34, -0.1],
          [0.28, 0.22],
          [-0.2, 0.24],
          [-0.34, 0.04],
        ] as const
      ).map(([dx, dz]): V3 => [xx + dx, 0.04, zz + dz]);
      if (edge.some(([a, , c]) => !inside(a, c) || a >= mooring.x0 - 0.08)) continue;
      const tone = 0.91 + (stoneIndex % 5) * 0.037;
      for (let j = 0; j < edge.length; j += 1) tri([xx, 0.045, zz], at(edge, (j + 1) % edge.length), at(edge, j), tone);
    }
  }
  const rockG = new THREE.BufferGeometry();
  rockG.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  rockG.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  rockG.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((positions.length / 3) * 2), 2));
  rockG.computeVertexNormals();
  primitive(root, kit.get('mooringRock'), rockG);
  // Small discontinuous strata lips run below the walking shelf; dark recesses stay separated vertically instead of
  // making coplanar decorative overlays.
  for (let k = 1; k < shelf.length - 1; k += 1) {
    const ring = at(shelf, k);
    for (let i = 0; i < n; i += 1) {
      if (i % 4 === 2) continue;
      const j = (i + 1) % n;
      const a = at(ring, i);
      const b = at(ring, j);
      const start: V3 = [a[0] + (b[0] - a[0]) * 0.1, a[1] + (b[1] - a[1]) * 0.1, a[2] + (b[2] - a[2]) * 0.1];
      const end: V3 = [a[0] + (b[0] - a[0]) * 0.86, a[1] + (b[1] - a[1]) * 0.86, a[2] + (b[2] - a[2]) * 0.86];
      const lip = beam(root, kit.get('mooringRock'), start, end, 0.095, 5);
      lip.updateMatrixWorld(true);
      const lp = lip.geometry.getAttribute('position');
      const lc: number[] = [];
      const v = new THREE.Vector3();
      for (let q = 0; q < lp.count; q += 1) {
        v.fromBufferAttribute(lp, q).applyMatrix4(lip.matrixWorld);
        lc.push(...stoneColor([v.x, v.y, v.z], 1.16).toArray());
      }
      lip.geometry.setAttribute('color', new THREE.Float32BufferAttribute(lc, 3));
    }
  }
  // Sparse inset-looking crack strokes on the shelf: narrow raised dark stone lines, depth-separated from the floor.
  for (const [a, b] of [
    [
      [7.12, 0.045, -1.45],
      [8.03, 0.045, -0.95],
    ],
    [
      [8.03, 0.045, -0.95],
      [8.43, 0.045, -0.99],
    ],
    [
      [9.85, 0.045, 1.61],
      [10.08, 0.045, 0.93],
    ],
    [
      [10.08, 0.045, 0.93],
      [10.68, 0.045, 0.59],
    ],
    [
      [5.3, 0.045, 2.75],
      [6.2, 0.045, 2.15],
    ],
  ] as const)
    line('trim', a, b, 0.021, 4);
};

// ---------------------------------------------------------------------------
// The ship. Rig: a gaff schooner (foremast x 43.5, mainmast x 36) because its
// fore-and-aft sails face the broadside street camera; square sails would be
// seen edge-on. Moored, the sails are brailed onto their booms and the gaffs
// lowered; under way (`flight`) they are set. Every fitting has a job: the
// load paths are mast step → partners → shrouds → chainplates, stays fore and
// aft, sheets and halyards to pins, lanterns on hooks, lights with a reason.
export const RIG = { main: { x: 36, top: 17.3, head: 12.9 }, fore: { x: 43.5, top: 15.4, head: 11.4 } } as const;

type Mast = { readonly x: number; readonly top: number; readonly head: number };
type Rect = { readonly x0: number; readonly x1: number; readonly z0: number; readonly z1: number };

const buildShip = (root: THREE.Object3D, kit: ShipKit, area: ShipArea): void => {
  const mat = (key: string): THREE.MeshStandardMaterial => kit.get(key);
  const withPart = <T extends THREE.Object3D>(o: T, part: string | null): T => (part === null ? o : tagMesh(o, { part }));
  const add = (key: string, g: THREE.BufferGeometry, part: string | null = null, cut = false): THREE.Mesh => withPart(primitive(root, mat(key), g, 0, 0, 0, cut), part);
  const B = (key: string, x: number, y: number, z: number, w: number, h: number, d: number, part: string | null = null, cut = false): THREE.Mesh => withPart(box(root, mat(key), x, y, z, w, h, d, cut), part);
  const L = (key: string, a: Vec3, b: Vec3, r = 0.06, n = 6, part: string | null = null): THREE.Mesh => withPart(beam(root, mat(key), a, b, r, n), part);
  const orient = (g: THREE.BufferGeometry, a: Vec3, b: Vec3): THREE.BufferGeometry => {
    const av = new THREE.Vector3(...a);
    const bv = new THREE.Vector3(...b);
    const v = bv.clone().sub(av);
    g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.clone().normalize()));
    const c = av.add(bv).multiplyScalar(0.5);
    g.translate(c.x, c.y, c.z);
    return g;
  };
  // Ropes carry a sway weight that is zero at both fixed ends.
  const rope = (a: Vec3, b: Vec3, r = 0.022, part: string | null = null, sway = 1, key = 'rope'): THREE.Mesh => {
    const len = new THREE.Vector3(...b).sub(new THREE.Vector3(...a)).length();
    const g = orient(new THREE.CylinderGeometry(r, r, len, 4, 6, true), a, b);
    if (key === 'rope') windAttribute(g, (_, t) => [Math.sin(Math.PI * t) * sway]);
    return add(key, g, part);
  };
  const ropePath = (points: readonly Vec3[], r = 0.022, part: string | null = null, sway = 1, key = 'rope'): void => {
    const n = points.length - 1;
    for (let i = 0; i < n; i += 1) {
      const a = at(points, i);
      const b = at(points, i + 1);
      const len = new THREE.Vector3(...b).sub(new THREE.Vector3(...a)).length();
      const g = orient(new THREE.CylinderGeometry(r, r, len, 4, 2, true), a, b);
      if (key === 'rope') windAttribute(g, (_, t) => [(Math.sin((Math.PI * (i + t)) / n)) * sway]);
      add(key, g, part);
    }
  };
  const { stern, bow, stem } = HULL;

  // --- Hull skin in strakes: bulwark, sheer strake, the painted gallery band with the lower-deck lights, bottom
  // planking, tarred garboards.
  add('bulwark', hullBand(0, 0, 2, true));
  add('hullWood', hullBand(0, -0.62, 2));
  add('hullPaint', hullBand(-0.62, -2.56, 5));
  add('hullWood', hullBand(-2.56, -4.7, 5));
  add('hullTar', hullBand(-4.7, -6.2, 6));
  // Wales: heavy rubbing strakes proud of the skin, running into the stem.
  for (const [y, r] of [
    [-0.62, 0.1],
    [-2.56, 0.12],
    [-0.02, 0.075],
  ] as const) {
    for (const side of [1, -1]) {
      const pts: V3[] = [];
      for (let x = stern + 0.02; x <= stem; x += 0.5) {
        const z = hullZ(x, y);
        if (z < 0.12 && x > bow) break;
        pts.push([x, y, side * (z + r * 0.55)]);
      }
      for (let i = 0; i < pts.length - 1; i += 1) L('wale', at(pts, i), at(pts, i + 1), r, 6);
    }
  }
  // Gunwale cap on the bulwark top, both sides; the stern is capped below.
  for (const side of [1, -1]) {
    let prev: V3 | null = null;
    for (let x = stern + 0.05; x <= stem - 0.15; x += 0.45) {
      const p: V3 = [x, sheer(x) + 0.03, side * (hullZ(x, sheer(x)) - 0.06)];
      if (prev !== null) L('rail', prev, p, 0.11, 4);
      prev = p;
    }
  }
  // Keel, forefoot and stem post along the lowest hull line.
  {
    const pts: V3[] = [];
    for (let x = stern; x <= stem + 0.01; x += 0.6) pts.push([Math.min(x, stem), -keelDepth(Math.min(x, stem)) - 0.16, 0]);
    pts.push([stem + 0.15, sheer(stem) + 0.35, 0]);
    for (let i = 0; i < pts.length - 1; i += 1) L('wale', at(pts, i), at(pts, i + 1), 0.2, 6);
  }

  // --- Flat transom with the gangway notch and the captain's stern lights.
  {
    const top = sheer(stern);
    const shape = new THREE.Shape();
    const half = hullZ(stern, 0) - 0.01;
    const ys: number[] = [];
    for (let k = 0; k <= 14; k += 1) ys.push(lerp(0, -keelDepth(stern), k / 14));
    shape.moveTo(-half, top);
    shape.lineTo(-1.55, top);
    shape.lineTo(-1.55, 0.03);
    shape.lineTo(1.55, 0.03);
    shape.lineTo(1.55, top);
    shape.lineTo(half, top);
    for (const y of ys) shape.lineTo(hullZ(stern, y) - 0.01, y);
    for (const y of [...ys].reverse()) shape.lineTo(-(hullZ(stern, y) - 0.01), y);
    const g = new THREE.ShapeGeometry(shape);
    const p = g.getAttribute('position');
    const uv = g.getAttribute('uv');
    for (let i = 0; i < p.count; i += 1) {
      const z = p.getX(i);
      const y = p.getY(i);
      p.setXYZ(i, stern, y, z);
      uv.setXY(i, z / 4, y / 4);
    }
    g.computeVertexNormals();
    // Swapping shape X → world z is a mirror, so the +Z shape face now faces aft.
    add('bulwark', g);
    // The painted gallery band continues across the transom.
    B('hullPaint', stern - 0.03, -1.59, 0, 0.06, 1.9, hullZ(stern, -1.6) * 2 - 0.2);
    B('wale', stern - 0.08, -0.62, 0, 0.14, 0.2, hullZ(stern, -0.62) * 2 + 0.1);
    B('wale', stern - 0.08, -2.56, 0, 0.16, 0.24, hullZ(stern, -2.56) * 2 + 0.1);
    for (const z of [-5.4, -3.1, 3.1, 5.4]) {
      B('wale', stern - 0.1, -1.55, z, 0.1, 1.0, 1.1);
      B('windowGlass', stern - 0.16, -1.55, z, 0.04, 0.78, 0.88);
      B('wale', stern - 0.18, -1.55, z, 0.03, 0.78, 0.06);
      B('wale', stern - 0.18, -1.55, z, 0.03, 0.06, 0.88);
      B('wale', stern - 0.14, -0.97, z, 0.2, 0.1, 1.3);
    }
    // Taffrail across the transom top, open at the gangway.
    for (const s of [-1, 1]) L('rail', [stern + 0.02, top + 0.03, s * 1.55], [stern + 0.02, top + 0.03, s * (half - 0.05)], 0.11, 4);
  }

  // --- Deck: textured planking from the walk rectangles, carried out to the hull and forward as the foredeck.
  const deckRects = area.surfaces.filter((s) => s.id.startsWith('ship_deck'));
  // Companionway mouths are cut out of the planking so their ladders show.
  const holes: Rect[] = area.portals.filter((p) => p.x > 28).map((p) => ({ x0: p.x - 0.62, x1: p.x + 0.62, z0: p.z - 0.62, z1: p.z + 0.62 }));
  const cut = (r: Rect, h: Rect): Rect[] => {
    if (h.x0 >= r.x1 || h.x1 <= r.x0 || h.z0 >= r.z1 || h.z1 <= r.z0) return [r];
    const out: Rect[] = [];
    if (h.x0 > r.x0) out.push({ ...r, x1: h.x0 });
    if (h.x1 < r.x1) out.push({ ...r, x0: h.x1 });
    const x0 = Math.max(r.x0, h.x0);
    const x1 = Math.min(r.x1, h.x1);
    if (h.z0 > r.z0) out.push({ x0, x1, z0: r.z0, z1: h.z0 });
    if (h.z1 < r.z1) out.push({ x0, x1, z0: h.z1, z1: r.z1 });
    return out;
  };
  for (const s of deckRects) {
    let rects: Rect[] = [{ x0: s.x0 <= 29.01 ? stern + 0.04 : s.x0, x1: s.x1, z0: s.z0 <= -7.99 ? -8.18 : s.z0, z1: s.z1 >= 7.99 ? 8.18 : s.z1 }];
    for (const h of holes) rects = rects.flatMap((r) => cut(r, h));
    for (const r of rects) B('deck', (r.x0 + r.x1) / 2, -0.06, (r.z0 + r.z1) / 2, r.x1 - r.x0, 0.12, r.z1 - r.z0);
  }
  {
    const shape = new THREE.Shape();
    const xs: number[] = [];
    for (let x = bow; x <= stem - 0.3; x += 0.4) xs.push(x);
    xs.push(stem - 0.3);
    const nose = 4.72;
    const fx = xs.filter((x) => x > FOC_X);
    shape.moveTo(FOC_X, -(hullZ(FOC_X, FOC) - 0.1));
    for (const x of fx) shape.lineTo(x, -(hullZ(x, FOC) - 0.1));
    for (const x of [...fx].reverse()) shape.lineTo(x, hullZ(x, FOC) - 0.1);
    shape.lineTo(FOC_X, hullZ(FOC_X, FOC) - 0.1);
    shape.lineTo(FOC_X, nose);
    shape.lineTo(FOC_NOSE, nose);
    shape.lineTo(FOC_NOSE, -nose);
    shape.lineTo(FOC_X, -nose);
    const g = new THREE.ShapeGeometry(shape);
    g.rotateX(Math.PI / 2);
    g.computeVertexNormals();
    const idx = g.getIndex();
    if (g.getAttribute('normal').getY(0) < 0 && idx !== null) {
      const a = idx.array;
      for (let i = 0; i < a.length; i += 3) {
        const t = a[i] ?? 0;
        a[i] = a[i + 2] ?? 0;
        a[i + 2] = t;
      }
      g.computeVertexNormals();
    }
    g.translate(0, FOC, 0);
    add('deck', g);
    // Deck under the bridge's windscreen nose, and the forecastle break: a planked bulkhead with a cap, with a
    // ladder up from each side passage.
    B('deck', (bow + FOC_NOSE) / 2, -0.06, 0, FOC_NOSE - bow, 0.12, nose * 2);
    const wall = (x0: number, z0: number, x1: number, z1: number): THREE.Mesh => {
      const o = B('bulwark', (x0 + x1) / 2, FOC / 2, (z0 + z1) / 2, Math.max(0.1, Math.abs(x1 - x0)), FOC, Math.max(0.1, Math.abs(z1 - z0)));
      B('rail', (x0 + x1) / 2, FOC + 0.02, (z0 + z1) / 2, Math.max(0.18, Math.abs(x1 - x0) + 0.08), 0.08, Math.max(0.18, Math.abs(z1 - z0) + 0.08));
      return o;
    };
    for (const q of [-1, 1]) {
      wall(FOC_X, q * nose, FOC_X, q * (hullZ(FOC_X, 0.5) - 0.05));
      wall(FOC_X, q * nose, FOC_NOSE, q * nose);
      const lz = q * 6.3;
      for (const dz of [-0.3, 0.3]) B('rail', FOC_X - 0.12, FOC / 2 + 0.1, lz + dz, 0.07, FOC + 0.2, 0.07);
      for (let k = 1; k < 4; k += 1) B('spar', FOC_X - 0.13, (k * FOC) / 4 + 0.05, lz, 0.06, 0.05, 0.6);
    }
    wall(FOC_NOSE, -nose, FOC_NOSE, nose);
  }
  // Covering board where the deck meets the bulwark (the waterway seam).
  for (const side of [1, -1]) L('rail', [stern + 0.1, 0.02, side * 8.13], [bow, 0.02, side * 8.13], 0.06, 4);

  // --- Balustrade: stanchions, turned balusters and a moulded cap rail on top of the solid bulwark; open at the
  // gangway and across the foredeck break.
  const balustrade = (path: readonly Vec3[], base: (p: Vec3) => number, cap: (p: Vec3) => number, posts = 2, spacing = 0.3): void => {
    let run = 0;
    for (let i = 0; i < path.length - 1; i += 1) {
      const a = at(path, i);
      const b = at(path, i + 1);
      const len = Math.hypot(b[0] - a[0], b[2] - a[2]);
      L('rail', [a[0], cap(a) + 0.02, a[2]], [b[0], cap(b) + 0.02, b[2]], 0.07, 4);
      L('rail', [a[0], base(a) + 0.1, a[2]], [b[0], base(b) + 0.1, b[2]], 0.045, 4);
      for (let d = (spacing - (run % spacing)) % spacing; d < len; d += spacing) {
        const t = d / len;
        const p: Vec3 = [lerp(a[0], b[0], t), 0, lerp(a[2], b[2], t)];
        if (cap(p) - base(p) > 0.3) L('baluster', [p[0], base(p) + 0.12, p[2]], [p[0], cap(p) - 0.03, p[2]], 0.032, 4);
      }
      run += len;
    }
    for (let i = 0; i < path.length; i += posts) {
      const p = at(path, i);
      if (cap(p) - base(p) > 0.3) B('rail', p[0], (base(p) + cap(p)) / 2 + 0.03, p[2], 0.15, cap(p) - base(p) + 0.1, 0.15);
    }
    const e = last(path);
    B('rail', e[0], (base(e) + cap(e)) / 2 + 0.03, e[2], 0.15, cap(e) - base(e) + 0.1, 0.15);
  };
  const onBulwark = (p: Vec3): number => sheer(p[0]);
  const capOf = (p: Vec3): number => railCap(p[0]);
  for (const side of [1, -1]) {
    const path: V3[] = [];
    for (let x = stern + 0.25; x < stem - 0.6; x += 0.9) path.push([x, 0, side * (hullZ(x, sheer(x)) - 0.07)]);
    path.push([stem - 0.45, 0, side * 0.28]);
    balustrade(path, onBulwark, capOf, 2);
  }
  for (const side of [1, -1]) {
    const path: V3[] = [];
    for (let z = 1.55; z <= hullZ(stern, 0.4) - 0.1; z += 0.9) path.push([stern + 0.12, 0, side * z]);
    path.push([stern + 0.12, 0, side * (hullZ(stern, 0.4) - 0.12)]);
    balustrade(path, onBulwark, capOf);
  }
  // Forecastle breastwork rail along the top of the break.
  const bridge = area.rooms.find((r) => r.type === 'cockpit');
  const bridgeHalf = bridge !== undefined ? ((bridge.rotation / 90) % 2 !== 0 ? bridge.width : bridge.depth) / 2 : 4;
  for (const side of [1, -1])
    balustrade(
      [
        [FOC_X + 0.05, 0, side * (bridgeHalf + 0.75)],
        [FOC_X + 0.05, 0, side * (hullZ(FOC_X, FOC) - 0.15)],
      ],
      () => FOC,
      () => FOC + 0.85,
      2,
    );
  // Gangway gate posts with lamps, the gate leaves swung outboard.
  for (const s of [-1, 1]) {
    B('rail', stern + 0.12, 0.72, s * 1.55, 0.2, 1.44, 0.2);
    B('iron', stern + 0.12, 1.47, s * 1.55, 0.26, 0.06, 0.26);
    lantern(root, kit, [stern + 0.12, 1.55, s * 1.55], true);
    for (const y of [0.3, 0.65, 0.98]) B('rail', stern - 0.62, y, s * 1.72, 1.1, 0.07, 0.07);
    B('rail', stern - 1.15, 0.64, s * 1.72, 0.09, 0.8, 0.09);
    L('rail', [stern - 0.1, 0.3, s * 1.72], [stern - 1.1, 0.98, s * 1.72], 0.03, 4);
  }

  // Scuppers: lead-lined drain holes at deck level that let rain off the deck (their drips are in the grime map).
  for (const side of [1, -1]) {
    for (let x = 30.5; x < 53; x += 3.1) {
      const z = side * (hullZ(x, -0.08) + 0.03);
      B('shade', x, -0.1, z, 0.2, 0.09, 0.04);
      B('iron', x, -0.17, z + side * 0.02, 0.26, 0.03, 0.06);
    }
  }
  // --- Lower-deck lights along both sides (they are the hatchery and farm below); frames proud of the skin, glass
  // that glows once the lamps are lit.
  for (const side of [1, -1]) {
    for (let x = 31.2, k = 0; x < 51; x += 2.45, k += 1) {
      const z = side * (hullZ(x, -1.5) + 0.05);
      const glass = (k * 3 + (side > 0 ? 1 : 2)) % 4 === 0 ? 'shade' : 'windowGlass';
      B('wale', x, -1.5, z, 0.94, 0.78, 0.1);
      B(glass, x, -1.5, z + side * 0.05, 0.72, 0.56, 0.04);
      B('wale', x, -1.5, z + side * 0.07, 0.72, 0.05, 0.03);
      B('wale', x, -1.02, z + side * 0.03, 1.08, 0.08, 0.14);
    }
  }

  // --- Chainplates, deadeyes and shrouds for both masts, with ratlines.
  for (const mast of [RIG.main, RIG.fore]) {
    for (const side of [1, -1]) {
      const feet = [-1.1, 0, 1.1].map((dx): V3 => {
        const x = mast.x + dx;
        const z = side * (hullZ(x, sheer(x)) + 0.1);
        B('iron', x, sheer(x) - 0.35, z, 0.07, 0.9, 0.05);
        const dead: V3 = [x, sheer(x) + 0.45, z];
        primitive(root, mat('wale'), new THREE.CylinderGeometry(0.11, 0.11, 0.07, 8).rotateX(Math.PI / 2), ...dead);
        return dead;
      });
      const heads = feet.map((_, k): V3 => [mast.x + (k - 1) * 0.12, mast.head - 0.25, side * 0.3]);
      for (const f of feet) rope(f, [f[0], f[1] + 0.25, f[2]], 0.04, null, 0, 'ropeTaut');
      // Shrouds and ratlines as one painted card spanning the three shrouds from the deadeye lanyards to the
      // hounds; v is scaled so the rungs sit 0.42 m apart.
      {
        const rows = 8;
        const pos: number[] = [];
        const uv: number[] = [];
        const index: number[] = [];
        const y0 = at(feet, 0)[1] + 0.25;
        for (let j = 0; j <= rows; j += 1) {
          for (let i = 0; i <= 2; i += 1) {
            const t = j / rows;
            const f = at(feet, i);
            const h = at(heads, i);
            const p: V3 = [lerp(f[0], h[0], t), lerp(y0, h[1], t), lerp(f[2], h[2], t)];
            pos.push(...p);
            uv.push(i / 2, (p[1] - y0) / (0.42 * 32));
          }
        }
        for (let j = 0; j < rows; j += 1) {
          for (let i = 0; i < 2; i += 1) {
            const a = j * 3 + i;
            index.push(a, a + 1, a + 3, a + 1, a + 4, a + 3);
          }
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
        g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
        g.setIndex(index);
        g.computeVertexNormals();
        const o = add('ratlineCard', g);
        o.renderOrder = 2;
      }
      // Topmast shrouds over the crosstrees.
      rope([mast.x, mast.head + 0.3, side * 1.05], [mast.x, mast.top - 0.5, side * 0.12], 0.022, null, 0.2, 'ropeTaut');
      rope([mast.x, mast.head + 0.3, side * 1.05], [mast.x, mast.head - 1.6, side * 0.25], 0.022, null, 0.2, 'ropeTaut');
    }
  }

  // --- Masts: stepped through the deck, wedged in partners under a tarred mast coat, iron-banded, with trestle- and
  // crosstrees at the doublings.
  for (const [key, mast] of [
    ['main', RIG.main],
    ['fore', RIG.fore],
  ] as const) {
    const x = mast.x;
    const lower = new THREE.CylinderGeometry(key === 'main' ? 0.19 : 0.18, key === 'main' ? 0.27 : 0.25, mast.head + 0.9, 10);
    primitive(root, mat('spar'), lower, x, (mast.head + 0.9) / 2 - 0.2, 0);
    const upper = new THREE.CylinderGeometry(0.08, 0.14, mast.top - mast.head + 0.6, 8);
    primitive(root, mat('spar'), upper, x - 0.02, mast.head + (mast.top - mast.head + 0.6) / 2 - 0.3, 0);
    primitive(root, mat('hullTar'), new THREE.CylinderGeometry(0.3, 0.4, 0.34, 10), x, 0.17, 0);
    for (const y of [2.2, 4.6, 7, 9.4]) if (y < mast.head - 0.6) primitive(root, mat('iron'), new THREE.CylinderGeometry(0.285 - 0.008 * y, 0.285 - 0.008 * y, 0.07, 10), x, y, 0);
    B('iron', x, mast.head + 0.5, 0, 0.72, 0.18, 0.42);
    B('spar', x, mast.head + 0.3, 0, 0.28, 0.14, 2.2);
    B('spar', x, mast.head + 0.3, 0, 1.1, 0.1, 0.24);
    primitive(root, mat('spar'), new THREE.SphereGeometry(0.13, 8, 5), x - 0.02, mast.top + 0.28, 0);
    // Fife rail round the mast foot: posts, a rail with belaying pins and the halyard falls coiled on them.
    for (const dx of [-0.62, 0.62]) for (const dz of [-0.62, 0.62]) B('rail', x + dx, 0.42, dz, 0.14, 0.84, 0.14);
    for (const s of [-1, 1]) {
      B('rail', x, 0.82, s * 0.62, 1.38, 0.1, 0.16);
      B('rail', x + s * 0.62, 0.82, 0, 0.16, 0.1, 1.38);
    }
    for (let k = -2; k <= 2; k += 1) for (const s of [-1, 1]) L('spar', [x + k * 0.24, 0.72, s * 0.62], [x + k * 0.24, 1.02, s * 0.62], 0.022, 4);
    for (const [dx, dz] of [
      [-0.24, -0.66],
      [0.24, 0.66],
      [0, -0.68],
      [-0.48, 0.66],
    ] as const)
      primitive(root, mat('ropeTaut'), new THREE.TorusGeometry(0.12, 0.035, 4, 10), x + dx, 0.62, dz);
    // Mast-foot lantern bracket (lit at night for working the halyards).
    B('iron', x + 0.3, 2.35, 0, 0.35, 0.04, 0.04);
    lantern(root, kit, [x + 0.47, 2.3, 0]);
  }
  // Spring stay between the mastheads, forestays to stem and bowsprit, and running backstays aft to the quarters.
  const bowspritEnd: Vec3 = [69.4, 3.45, 0];
  const stemHead: Vec3 = [stem - 0.1, sheer(stem) + 0.3, 0];
  rope([RIG.main.x, RIG.main.head + 0.2, 0], [RIG.fore.x, RIG.fore.head + 0.1, 0], 0.03, null, 0.1, 'ropeTaut');
  rope([RIG.main.x, RIG.main.top - 0.3, 0], [RIG.fore.x, RIG.fore.top - 0.2, 0], 0.022, null, 0.1, 'ropeTaut');
  rope([RIG.fore.x, RIG.fore.head + 0.15, 0], stemHead, 0.032, null, 0.05, 'ropeTaut');
  rope([RIG.fore.x, RIG.fore.top - 0.25, 0], bowspritEnd, 0.028, null, 0.05, 'ropeTaut');
  for (const s of [-1, 1]) rope([RIG.main.x, RIG.main.top - 0.4, s * 0.1], [stern + 1.2, sheer(stern) + 0.7, s * 7.55], 0.022, null, 0.15, 'ropeTaut');

  // Cutwater: the knee of the head, a curved timber forward of the stem that carries the bowsprit gammoning and the
  // bobstay; it gives the bow its rake.
  {
    const pts: readonly (readonly [number, number])[] = [
      [stem - 0.1, sheer(stem) + 0.25],
      [stem + 0.75, 1.15],
      [stem + 1.05, 0.1],
      [stem + 0.85, -0.9],
      [stem + 0.35, -1.7],
      [stem - 0.3, -2.15],
    ];
    for (let i = 0; i < pts.length - 1; i += 1) {
      const a = at(pts, i);
      const b = at(pts, i + 1);
      L('wale', [a[0], a[1], 0], [b[0], b[1], 0], 0.17, 6);
    }
    for (const [x, y] of [
      [stem + 0.55, 0.85],
      [stem + 0.7, 0.2],
    ] as const)
      for (const q of [-1, 1]) B('iron', x, y, q * 0.19, 0.3, 0.08, 0.04);
  }
  // Bowsprit with its bobstay chain and whisker shrouds; the jibs set on it.
  L('spar', [stem - 1.6, sheer(stem) + 0.05, 0], bowspritEnd, 0.19, 8);
  primitive(root, mat('iron'), new THREE.CylinderGeometry(0.2, 0.2, 0.14, 8).rotateZ(Math.PI / 2 + 0.1), 66, 2.72, 0);
  rope(bowspritEnd, [stem - 0.05, -1.1, 0], 0.035, null, 0, 'ropeTaut');
  for (const s of [-1, 1]) rope(bowspritEnd, [stem - 3, sheer(stem - 3) + 0.2, s * (hullZ(stem - 3, 1) + 0.15)], 0.024, null, 0, 'ropeTaut');

  // --- Booms (always rigged) and the two gaff sails.
  type Sail = { readonly mat: string; readonly mast: Mast; readonly tack: readonly [number, number]; readonly clew: readonly [number, number]; readonly throat: readonly [number, number]; readonly peak: readonly [number, number] };
  const sails: readonly Sail[] = [
    { mat: 'mainsail', mast: RIG.main, tack: [RIG.main.x - 0.3, 3.55], clew: [stern - 0.35, 3.85], throat: [RIG.main.x - 0.3, RIG.main.head - 0.9], peak: [stern + 0.25, RIG.main.head + 2.3] },
    { mat: 'foresail', mast: RIG.fore, tack: [RIG.fore.x - 0.3, 3.6], clew: [RIG.main.x + 1.25, 3.85], throat: [RIG.fore.x - 0.3, RIG.fore.head - 0.8], peak: [RIG.main.x + 1.6, RIG.fore.head + 2] },
  ];
  for (const s of sails) {
    const [tx, ty] = s.tack;
    const [cx, cy] = s.clew;
    L('spar', [s.mast.x - 0.05, ty - 0.05, 0], [cx - 0.35, cy - 0.05, 0], 0.13, 8);
    B('iron', s.mast.x - 0.25, ty - 0.05, 0, 0.2, 0.2, 0.34); // gooseneck
    // The topping lift holds the boom end up whether or not the sail is set.
    rope([s.mast.x, s.mast.head + 0.1, 0], [cx - 0.2, cy, 0], 0.018, null, 0.35);
    // Main sheet: boom end to a traveller on the taffrail / deck.
    if (s.mat === 'mainsail') for (const q of [-1, 1]) rope([cx + 0.1, cy - 0.1, 0], [stern + 0.12, sheer(stern) + 0.66, q * 2.3], 0.022, null, 0.3);
    else rope([cx + 0.1, cy - 0.1, 0], [s.mast.x + 0.72, 0.9, 0], 0.022, null, 0.3);
    // --- Set (flight): the sail, the gaff raised to the peak, taut halyards.
    const cols = 12;
    const rows = 10;
    const pos: number[] = [];
    const uv: number[] = [];
    const weight: number[] = [];
    const corner = (u: number, v: number): readonly [number, number] => {
      const foot = [lerp(tx, cx, u), lerp(ty, cy, u)] as const;
      const head = [lerp(s.throat[0], s.peak[0], u), lerp(s.throat[1], s.peak[1], u)] as const;
      return [lerp(foot[0], head[0], v), lerp(foot[1], head[1], v)];
    };
    for (let j = 0; j <= rows; j += 1) {
      for (let i = 0; i <= cols; i += 1) {
        const u = i / cols;
        const v = j / rows;
        const [x, y] = corner(u, v);
        const w = Math.sin(Math.PI * u) * Math.pow(Math.sin(Math.PI * Math.min(1, v * 1.08)), 0.8);
        // Indexed so the belly shades smoothly; the world de-indexes when batching.
        pos.push(x, y, 0.08 + w * 0.35);
        uv.push(1 - u, v);
        weight.push(w);
      }
    }
    const index: number[] = [];
    for (let j = 0; j < rows; j += 1) {
      for (let i = 0; i < cols; i += 1) {
        const a = j * (cols + 1) + i;
        index.push(a, a + 1, a + cols + 1, a + 1, a + cols + 2, a + cols + 1);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(index);
    g.computeVertexNormals();
    windAttribute(g, (_, __, i) => [at(weight, i), s.mast.x * 0.37, 0, 0]);
    add(s.mat, g, 'sailSet');
    L('spar', [s.mast.x - 0.1, s.throat[1] + 0.05, 0.08], [s.peak[0] - 0.3, s.peak[1] + 0.08, 0.08], 0.1, 8, 'sailSet');
    B('iron', s.mast.x - 0.25, s.throat[1] + 0.05, 0, 0.22, 0.22, 0.36, 'sailSet');
    rope([s.mast.x, s.mast.head + 0.05, 0], [s.mast.x - 0.25, s.throat[1] + 0.2, 0], 0.022, 'sailSet', 0.2);
    rope([s.mast.x, s.mast.top - 1.5, 0], [lerp(s.throat[0], s.peak[0], 0.55), lerp(s.throat[1], s.peak[1], 0.55) + 0.12, 0.08], 0.02, 'sailSet', 0.4);
    rope([s.mast.x, s.mast.top - 1.5, 0], [s.peak[0], s.peak[1] + 0.1, 0.08], 0.02, 'sailSet', 0.4);
    // Mast hoops lacing the luff.
    for (let y = ty + 0.6; y < s.throat[1]; y += 0.9) {
      const h = new THREE.TorusGeometry(0.3, 0.03, 4, 10);
      h.rotateX(Math.PI / 2);
      add('spar', h.translate(s.mast.x, y, 0), 'sailSet');
    }
    // --- Furled (moored): canvas brailed along the boom under its gaskets, the gaff lowered onto it, hoops stacked
    // at the foot of the mast.
    const fl = new THREE.Vector3(cx - tx, cy - ty, 0).length();
    const fg = new THREE.CylinderGeometry(0.24, 0.2, fl - 0.6, 8, 1);
    fg.rotateZ(Math.PI / 2 + Math.atan2(cy - ty, cx - tx) + Math.PI);
    add('furl', fg.translate((tx + cx) / 2 - 0.1, (ty + cy) / 2 + 0.22, 0), 'sailFurled');
    for (let u = 0.08; u < 0.95; u += 0.13) {
      const x = lerp(tx, cx, u) - 0.2;
      const y = lerp(ty, cy, u) + 0.22;
      const gk = new THREE.TorusGeometry(0.25, 0.025, 3, 8);
      gk.rotateY(Math.PI / 2);
      add('ropeTaut', gk.translate(x, y, 0), 'sailFurled');
    }
    L('spar', [s.mast.x - 0.1, ty + 0.55, 0], [cx + 0.2, cy + 0.52, 0], 0.1, 8, 'sailFurled');
    for (let k = 0; k < 5; k += 1) {
      const h = new THREE.TorusGeometry(0.3, 0.03, 4, 10);
      h.rotateX(Math.PI / 2);
      add('spar', h.translate(s.mast.x, ty + 0.72 + k * 0.07, 0), 'sailFurled');
    }
    rope([s.mast.x, s.mast.head + 0.05, 0], [s.mast.x - 0.25, ty + 0.7, 0], 0.02, 'sailFurled', 0.25);
  }
  // Fore topsail (a topsail schooner's square sail): yards crossed at the fore doublings, braced aft to the mainmast.
  // Furled on its yard at the mooring; set under way, sheeted to the lower yard and bellying forward.
  {
    const f = RIG.fore;
    const xs = f.x + 0.3;
    const yTop = f.head + 1.45;
    const yLow = f.head - 0.15;
    const half = 3.2;
    L('spar', [xs, yTop, -half], [xs, yTop, half], 0.09, 6);
    L('spar', [xs, yLow, -half - 0.4], [xs, yLow, half + 0.4], 0.11, 6);
    B('iron', xs - 0.12, yTop, 0, 0.2, 0.2, 0.3);
    B('iron', xs - 0.12, yLow, 0, 0.22, 0.22, 0.34);
    for (const q of [-1, 1]) {
      rope([xs, yTop, q * (half - 0.1)], [RIG.main.x + 0.2, RIG.main.head + 1.4, q * 0.2], 0.016, null, 0.2, 'ropeTaut');
      rope([xs, yLow, q * (half + 0.3)], [RIG.main.x + 0.2, RIG.main.head - 0.6, q * 0.25], 0.016, null, 0.2, 'ropeTaut');
      rope([f.x, f.top - 0.4, 0], [xs, yTop, q * (half - 0.1)], 0.014, null, 0.1, 'ropeTaut');
    }
    const fg = new THREE.CylinderGeometry(0.17, 0.17, half * 2 - 0.3, 8);
    fg.rotateX(Math.PI / 2);
    add('furl', fg.translate(xs + 0.03, yTop - 0.17, 0), 'sailFurled');
    for (let z = -half + 0.4; z < half; z += 0.8) {
      const gk = new THREE.TorusGeometry(0.18, 0.02, 3, 8);
      add('ropeTaut', gk.translate(xs + 0.03, yTop - 0.17, z), 'sailFurled');
    }
    const cols = 8;
    const rows = 6;
    const pos: number[] = [];
    const uv: number[] = [];
    const w: number[] = [];
    const index: number[] = [];
    for (let j = 0; j <= rows; j += 1) {
      for (let i = 0; i <= cols; i += 1) {
        const u = i / cols;
        const v = j / rows;
        const hw = lerp(half + 0.3, half - 0.1, v);
        const z = lerp(-hw, hw, u);
        const y = lerp(yLow + 0.1, yTop - 0.08, v);
        const wt = Math.sin(Math.PI * u) * Math.sin(Math.PI * Math.min(1, v * 1.1 + 0.08));
        pos.push(xs + 0.06 + wt * 0.25, y, z);
        uv.push(u, v);
        w.push(wt);
      }
    }
    for (let j = 0; j < rows; j += 1) {
      for (let i = 0; i < cols; i += 1) {
        const a = j * (cols + 1) + i;
        index.push(a, a + cols + 1, a + 1, a + 1, a + cols + 1, a + cols + 2);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(index);
    g.computeVertexNormals();
    windAttribute(g, (_, __, i) => [at(w, i) * 0.8, 3.1, 1, 0]);
    add('foresail', g, 'sailSet');
  }
  // Jibs: the staysail on the forestay, the jib on the topmast stay. Furled, they are stowed as bundles along the
  // bowsprit and stemhead.
  const stayPoint = (a: Vec3, b: Vec3, t: number): V3 => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
  const jibs: readonly { readonly a: Vec3; readonly b: Vec3; readonly t0: number; readonly t1: number; readonly clew: readonly [number, number] }[] = [
    { a: [RIG.fore.x, RIG.fore.head + 0.15, 0], b: stemHead, t0: 0.3, t1: 0.93, clew: [stem - 6.3, 4.1] },
    { a: [RIG.fore.x, RIG.fore.top - 0.25, 0], b: bowspritEnd, t0: 0.34, t1: 0.94, clew: [stem - 0.8, 4.6] },
  ];
  for (const j of jibs) {
    const head = stayPoint(j.a, j.b, j.t0);
    const tack = stayPoint(j.a, j.b, j.t1);
    const clew: Vec3 = [j.clew[0], j.clew[1], 0];
    const pos: number[] = [];
    const uv: number[] = [];
    const w: number[] = [];
    const n = 8;
    const P = (i: number, k: number): readonly [number, number, number, number, number, number] => {
      const u = i / n;
      const v = k / n;
      const x = head[0] * (1 - u - v) + tack[0] * u + clew[0] * v;
      const y = head[1] * (1 - u - v) + tack[1] * u + clew[1] * v;
      const wt = Math.min(1, 4 * u * v * 1.6 + Math.sin(Math.PI * u) * v * 1.2) * (1 - u - v > 0 ? 1 : 0) * 0.9;
      return [x, y, 0.05 + wt * 0.3, 1 - v, u, wt];
    };
    for (let r = 0; r < n; r += 1) {
      for (let c = 0; c < n - r; c += 1) {
        const tris = [[P(c, r), P(c + 1, r), P(c, r + 1)]];
        if (c < n - r - 1) tris.push([P(c + 1, r), P(c + 1, r + 1), P(c, r + 1)]);
        for (const t of tris) {
          for (const p of t) {
            pos.push(p[0], p[1], p[2]);
            uv.push(p[3], p[4]);
            w.push(p[5]);
          }
        }
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.computeVertexNormals();
    windAttribute(g, (_, __, i) => [at(w, i), j.clew[0], 0, 0]);
    add('foresail', g, 'sailSet');
    rope(clew, [clew[0] - 2.4, sheer(clew[0] - 2.4) + 0.5, 5.2], 0.018, 'sailSet', 0.5);
    const inner = stayPoint(j.a, j.b, j.t1 - 0.12);
    const bv = new THREE.Vector3(...tack).sub(new THREE.Vector3(...inner));
    const bg = new THREE.CylinderGeometry(0.16, 0.12, bv.length(), 6);
    bg.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), bv.normalize()));
    const c = stayPoint(j.a, j.b, j.t1 - 0.06);
    add('furl', bg.translate(c[0], c[1] - 0.12, c[2]), 'sailFurled');
  }

  // --- Flags: the ensign on a staff at the port quarter while moored (hoisted to the main gaff peak under way), a
  // long pennant at the main truck.
  const flag = (hoist: Vec3, len: number, height: number, key: string, part: string | null, taper = 0): THREE.Mesh => {
    const g = new THREE.PlaneGeometry(len, height, 12, 2);
    g.translate(-len / 2, 0, 0);
    const p = g.getAttribute('position');
    for (let i = 0; i < p.count; i += 1) {
      const d = -p.getX(i);
      const k = d / len;
      p.setY(i, p.getY(i) * (1 - taper * k));
    }
    g.translate(...hoist);
    windAttribute(g, (v) => [hoist[0] - v.x, len]);
    return add(key, g, part);
  };
  L('spar', [stern + 0.35, 0.4, -6.9], [stern - 0.35, 4.5, -7.4], 0.05, 6);
  primitive(root, mat('brass'), new THREE.SphereGeometry(0.07, 6, 4), stern - 0.36, 4.55, -7.4);
  flag([stern - 0.3, 3.95, -7.37], 1.7, 1.05, 'flag', 'ensignMoored');
  flag([stern - 0.3, 3.95, -7.355], 1.7, 0.3, 'flagCream', 'ensignMoored');
  const mainPeak = at(sails, 0).peak;
  flag([mainPeak[0] - 0.2, mainPeak[1] - 0.3, 0.08], 1.9, 1.15, 'flag', 'ensignFlying');
  flag([RIG.main.x - 0.1, RIG.main.top + 0.1, 0], 5.4, 0.34, 'flag', null, 0.85);
  flag([RIG.fore.x - 0.1, RIG.fore.top + 0.1, 0], 2.6, 0.26, 'flagCream', null, 0.8);

  // --- Crew laundry on a line between the masts, out by day in fair weather, taken in at dusk and before casting
  // off. Cut-tagged so Brad shows through.
  {
    const a: Vec3 = [RIG.main.x + 0.35, 2.55, 0.32];
    const b: Vec3 = [RIG.fore.x - 0.35, 2.5, 0.32];
    const wash = catenary(a, b, 0.28, 12);
    ropePath(wash, 0.014, 'laundry', 0.8);
    const along = (t: number): V3 => {
      const i = Math.min(11, Math.floor(t * 12));
      const f = t * 12 - i;
      const p = at(wash, i);
      const q = at(wash, i + 1);
      return [lerp(p[0], q[0], f), lerp(p[1], q[1], f), lerp(p[2], q[2], f)];
    };
    for (const [t, w, h, key] of [
      [0.12, 0.62, 0.72, 'laundryWhite'],
      [0.24, 0.5, 0.55, 'laundryBlue'],
      [0.38, 0.7, 0.8, 'laundryWhite'],
      [0.5, 0.34, 0.34, 'laundryRed'],
      [0.63, 0.56, 0.62, 'laundryTan'],
      [0.78, 0.66, 0.5, 'laundryWhite'],
      [0.89, 0.4, 0.46, 'laundryBlue'],
    ] as const) {
      const top = along(t);
      const g = new THREE.PlaneGeometry(w, h, 4, 3);
      g.translate(top[0], top[1] - h / 2, top[2]);
      windAttribute(g, (v) => [top[1] - v.y, t * 9]);
      add(key, g, 'laundry', true);
      for (const dx of [-w / 2 + 0.05, w / 2 - 0.05]) B('wale', top[0] + dx, top[1] + 0.02, top[2], 0.03, 0.07, 0.03, 'laundry');
    }
  }

  // --- Foredeck: the windlass for the grapnel, catheads, and the anchor catted.
  {
    const x = 57.2;
    const f = FOC;
    B('wale', x, f + 0.45, -1.05, 0.34, 0.9, 0.3);
    B('wale', x, f + 0.45, 1.05, 0.34, 0.9, 0.3);
    const barrel = new THREE.CylinderGeometry(0.3, 0.3, 1.8, 10);
    barrel.rotateX(Math.PI / 2);
    primitive(root, mat('spar'), barrel, x, f + 0.42, 0);
    for (const z of [-0.55, 0, 0.55]) primitive(root, mat('iron'), new THREE.CylinderGeometry(0.32, 0.32, 0.06, 10).rotateX(Math.PI / 2), x, f + 0.42, z);
    for (const z of [-1.25, 1.25]) L('iron', [x, f + 0.42, z], [x + 0.05, f + 1.05, z], 0.035, 4);
    ropePath(catenary([x + 0.25, f + 0.5, 0.9], [60.2, sheer(60.2) + 0.2, 4.9], 0.25, 6), 0.035, null, 0, 'iron');
    for (const s of [-1, 1]) L('wale', [59.4, sheer(59.4) + 0.05, s * 3.2], [60.1, sheer(60.1) + 0.25, s * (hullZ(60.1, 1) + 0.75)], 0.13, 6);
    // The grapnel hanging from the starboard cathead on its chain.
    const ax = 60.15;
    const az = hullZ(60.1, 1) + 0.72;
    const top = sheer(60.1) + 0.15;
    L('iron', [ax, top, az], [ax, top - 1.25, az], 0.03, 4);
    L('iron', [ax, top - 1.25, az], [ax, top - 2.9, az], 0.07, 6);
    for (let k = 0; k < 4; k += 1) {
      const a = (k * Math.PI) / 2 + 0.4;
      const dx = Math.cos(a) * 0.42;
      const dz = Math.sin(a) * 0.42;
      L('iron', [ax, top - 2.85, az], [ax + dx, top - 2.55, az + dz], 0.05, 4);
      L('iron', [ax + dx, top - 2.55, az + dz], [ax + dx * 1.1, top - 2.25, az + dz * 1.1], 0.04, 4);
    }
    primitive(root, mat('iron'), new THREE.TorusGeometry(0.14, 0.03, 4, 10), ax, top - 1.2, az);
    // A white steaming light on the foremast facing forward (port red and starboard green are on the bridge).
    B('iron', RIG.fore.x + 0.28, 6.1, 0, 0.3, 0.05, 0.05);
    lantern(root, kit, [RIG.fore.x + 0.4, 6.05, 0]);
  }

  // --- Galley stovepipe ("Charlie Noble") through the foredeck: the crew's stove is below the bow. Iron pipe, deck
  // collar, rain cowl.
  {
    const x = 55.4;
    const z = -2.6;
    const f = FOC;
    primitive(root, mat('iron'), new THREE.CylinderGeometry(0.24, 0.3, 0.12, 8), x, f + 0.06, z);
    primitive(root, mat('iron'), new THREE.CylinderGeometry(0.12, 0.12, 1.5, 8), x, f + 0.8, z);
    primitive(root, mat('iron'), new THREE.CylinderGeometry(0.3, 0.13, 0.2, 8), x, f + 1.66, z);
    for (const a of [0, 2.1, 4.2]) L('iron', [x + Math.cos(a) * 0.12, f + 1.5, z + Math.sin(a) * 0.12], [x + Math.cos(a) * 0.26, f + 1.62, z + Math.sin(a) * 0.26], 0.015, 3);
  }

  // --- Rudder hung on the sternpost with iron pintles, under the gangway.
  {
    const shape = new THREE.Shape();
    shape.moveTo(0, -0.9);
    shape.lineTo(-1.7, -1.25);
    shape.lineTo(-2.05, -4.5);
    shape.lineTo(-0.2, -keelDepth(stern) + 0.05);
    shape.lineTo(0, -keelDepth(stern) + 0.05);
    shape.closePath();
    const g = new THREE.ExtrudeGeometry(shape, { depth: 0.26, bevelEnabled: false });
    g.translate(stern - 0.02, 0, -0.13);
    primitive(root, mat('hullWood'), g);
    for (const y of [-1.4, -2.6, -3.8]) B('iron', stern - 0.45, y, 0, 1, 0.1, 0.3);
  }

  // --- Lift stones: aether crystals in brass-banded iron cradles bolted to the flanks on heavy knees, three a side
  // so the lift is balanced. They are what keeps the ship aloft: a faint glow by day, bright at night.
  for (const side of [1, -1]) {
    for (const x of [33.2, 40.8, 48.4]) {
      const y = -3.35;
      const zs = side * hullZ(x, y);
      const z = zs + side * 1.05;
      const g = new THREE.OctahedronGeometry(0.5, 0);
      g.scale(1, 1.45, 1);
      g.rotateY(0.5);
      primitive(root, mat('liftStone'), g, x, y, z);
      primitive(root, mat('iron'), new THREE.TorusGeometry(0.62, 0.055, 4, 12).rotateX(Math.PI / 2), x, y + 0.42, z);
      primitive(root, mat('iron'), new THREE.TorusGeometry(0.62, 0.055, 4, 12).rotateX(Math.PI / 2), x, y - 0.42, z);
      for (let k = 0; k < 4; k += 1) {
        const a = (k * Math.PI) / 2 + 0.4;
        const dx = Math.cos(a) * 0.62;
        const dz = Math.sin(a) * 0.62;
        L('iron', [x + dx, y + 0.42, z + dz], [x + dx * 0.25, y + 0.95, z + dz * 0.25], 0.035, 4);
        L('iron', [x + dx, y - 0.42, z + dz], [x + dx * 0.25, y - 0.95, z + dz * 0.25], 0.035, 4);
      }
      primitive(root, mat('brass'), new THREE.CylinderGeometry(0.14, 0.1, 0.12, 6), x, y + 0.98, z);
      primitive(root, mat('brass'), new THREE.CylinderGeometry(0.1, 0.14, 0.12, 6), x, y - 0.98, z);
      // Knees: an upper strut and a lower brace from the hull to the cradle.
      L('wale', [x, y + 0.98, z], [x, y + 1.6, side * (hullZ(x, y + 1.6) - 0.02)], 0.09, 6);
      L('wale', [x, y - 0.98, z], [x, y - 2.2, side * (hullZ(x, y - 2.2) - 0.02)], 0.09, 6);
      B('iron', x, y + 1.6, side * (hullZ(x, y + 1.6) + 0.02), 0.34, 0.5, 0.06);
      B('iron', x, y - 2.2, side * (hullZ(x, y - 2.2) + 0.02), 0.34, 0.5, 0.06);
    }
  }

  // --- Timberheads: where the bulwark is chest-high (poop and bow) its inner face shows the frame heads that carry
  // it, with a plank sheathing cap.
  for (const side of [1, -1]) {
    for (let x = stern + 0.4; x < stem - 1.2; x += 0.95) {
      const top = sheer(x);
      if (railCap(x) - top > 0.3) continue;
      const z = side * (hullZ(x, top) - 0.13);
      B('rail', x, top / 2, z, 0.13, top, 0.1);
    }
  }
  for (let z = -hullZ(stern, 0.5) + 0.5; z < hullZ(stern, 0.5) - 0.4; z += 0.95) if (Math.abs(z) > 1.7) B('rail', stern + 0.13, sheer(stern) / 2, z, 0.1, sheer(stern), 0.13);
  // --- Jetty cargo waiting to be swayed aboard (its own collider): crates, casks, sacks.
  {
    const c = area.colliders.find((q) => q.id === 'jetty_cargo');
    if (c !== undefined) {
      for (const [x, z, w, h, d] of [
        [12.75, 4.55, 0.85, 0.8, 0.8],
        [13.7, 4.6, 0.9, 0.7, 0.85],
        [12.8, 5.3, 0.75, 0.6, 0.7],
        [12.9, 4.6, 0.6, 0.5, 0.6],
      ] as const) {
        const y = x === 12.9 ? 0.8 + 0.25 : h / 2;
        B('spar', x, y, z, w, h, d, 'mooring');
        for (const q of [-1, 1]) B('wale', x + q * (w / 2 - 0.04), y, z, 0.08, h + 0.01, d + 0.01, 'mooring');
      }
      for (const [x, z] of [
        [14.15, 5.35],
        [13.55, 5.35],
      ] as const) {
        withPart(primitive(root, mat('spar'), new THREE.CylinderGeometry(0.3, 0.3, 0.75, 10), x, 0.375, z), 'mooring');
        for (const y of [0.12, 0.63]) withPart(primitive(root, mat('iron'), new THREE.CylinderGeometry(0.31, 0.31, 0.04, 10), x, y, z), 'mooring');
      }
      const sack = new THREE.SphereGeometry(0.32, 7, 5);
      sack.scale(1.2, 0.6, 1);
      withPart(primitive(root, mat('furl'), sack, 14.3, 0.2, 4.45), 'mooring');
    }
  }

  // --- Fixtures on their own colliders (kind "fixture" in the record).
  const fixture = (id: string): Rect | undefined => area.colliders.find((c) => c.id === id);
  // Fresh-water casks on chocks, hooped, lashed to the bulwark eye bolts.
  {
    const c = fixture('ship_water_casks');
    if (c !== undefined) {
      const n = 3;
      const w = (c.x1 - c.x0) / n;
      B('wale', (c.x0 + c.x1) / 2, 0.08, (c.z0 + c.z1) / 2, c.x1 - c.x0, 0.16, 0.5);
      for (let k = 0; k < n; k += 1) {
        const x = c.x0 + w * (k + 0.5);
        const z = (c.z0 + c.z1) / 2;
        primitive(root, mat('spar'), new THREE.CylinderGeometry(0.44, 0.44, 0.95, 10), x, 0.62, z);
        primitive(root, mat('spar'), new THREE.CylinderGeometry(0.5, 0.5, 0.52, 10), x, 0.62, z);
        for (const y of [0.2, 0.42, 0.82, 1.04]) {
          const rr = y === 0.42 || y === 0.82 ? 0.505 : 0.455;
          primitive(root, mat('iron'), new THREE.CylinderGeometry(rr, rr, 0.05, 10), x, y, z);
        }
        B('rail', x, 1.1, z, 0.3, 0.04, 0.3);
      }
      for (const y of [0.55, 0.85]) rope([c.x0 + 0.05, y, c.z1 - 0.05], [c.x1 - 0.05, y, c.z1 - 0.05], 0.025, null, 0, 'ropeTaut');
    }
  }
  // Skylight over the rooms below: a raised coaming with hinged glazed lids, glowing from the lamps beneath at night.
  {
    const c = fixture('ship_skylight');
    if (c !== undefined) {
      const x = (c.x0 + c.x1) / 2;
      const z = (c.z0 + c.z1) / 2;
      const w = c.x1 - c.x0 - 0.1;
      const d = c.z1 - c.z0 - 0.1;
      B('rail', x, 0.22, z, w, 0.44, d);
      B('windowGlass', x, 0.47, z, w - 0.2, 0.02, d - 0.2);
      // Pitched glazed lids on a ridge: glass panes between dark glazing bars, protected by brass guard rods.
      for (const s of [-1, 1]) {
        const pane = new THREE.BoxGeometry(w - 0.14, 0.03, d / 2 - 0.1);
        pane.rotateX(s * 0.32);
        primitive(root, mat('windowGlass'), pane, x, 0.6, z + s * (d / 4 - 0.02));
        for (let k = 0; k <= 4; k += 1) {
          const bar = new THREE.BoxGeometry(0.05, 0.07, d / 2 - 0.06);
          bar.rotateX(s * 0.32);
          primitive(root, mat('wale'), bar, x - w / 2 + 0.07 + (k * (w - 0.14)) / 4, 0.62, z + s * (d / 4 - 0.02));
        }
        for (let k = 1; k < 4; k += 1) {
          const rod = new THREE.CylinderGeometry(0.012, 0.012, d / 2 - 0.1, 4);
          rod.rotateX(Math.PI / 2 + s * 0.32);
          primitive(root, mat('brass'), rod, x - w / 2 + (k * w) / 4, 0.7, z + s * (d / 4 - 0.02));
        }
      }
      B('wale', x, 0.72, z, w, 0.08, 0.1);
      B('brass', x, 0.52, z, 0.08, 0.08, d - 0.1);
    }
  }
  // Companionways at the portals: a coaming round the opening, the sliding hatch run back on its slides, and the
  // ladder going down into the dark.
  for (const p of area.portals.filter((q) => q.x > 28)) {
    const half = 0.62;
    B('shade', p.x, -1.4, p.z, half * 2, 0.04, half * 2);
    for (const s of [-1, 1]) {
      B('wale', p.x + s * (half - 0.03), -0.7, p.z, 0.06, 1.4, half * 2);
      B('wale', p.x, -0.7, p.z + s * (half - 0.03), half * 2, 1.4, 0.06);
    }
    for (const s of [-1, 1]) {
      B('rail', p.x + s * half, 0.07, p.z, 0.14, 0.2, half * 2 + 0.14);
      B('rail', p.x, 0.07, p.z + s * half, half * 2 - 0.14, 0.2, 0.14);
    }
    for (let k = 0; k < 4; k += 1) B('spar', p.x, -0.08 - k * 0.28, p.z - half + 0.2 + k * 0.12, half * 1.4, 0.05, 0.12);
    for (const s of [-1, 1]) L('spar', [p.x + s * half * 0.72, 0.02, p.z - half + 0.14], [p.x + s * half * 0.72, -1.1, p.z - half + 0.62], 0.04, 4);
    for (const s of [-1, 1]) B('iron', p.x + s * (half + 0.1), 0.19, p.z - half - 0.55, 0.06, 0.04, 1.3);
    B('rail', p.x, 0.28, p.z - half - 0.58, half * 2 + 0.1, 0.08, 1.16);
    for (let k = 0; k < 5; k += 1) B('wale', p.x, 0.33, p.z - half - 1.08 + k * 0.25, half * 2 + 0.06, 0.02, 0.06);
    B('brass', p.x, 0.34, p.z - half - 0.02, 0.3, 0.05, 0.05);
  }
  // Pin rails inside the bulwark at the shrouds: halyards and sheets belay here.
  for (const mast of [RIG.main, RIG.fore]) {
    for (const side of [1, -1]) {
      const x = mast.x;
      const z = side * 7.72;
      B('rail', x, 0.72, z, 2.8, 0.09, 0.16);
      for (let k = -5; k <= 5; k += 1) L('spar', [x + k * 0.25, 0.62, z], [x + k * 0.25, 0.9, z], 0.02, 4);
      for (const dx of [-0.9, 0.4, 1.1]) primitive(root, mat('ropeTaut'), new THREE.TorusGeometry(0.14, 0.035, 4, 10), x + dx, 0.55, z - side * 0.05);
    }
  }
  // Sky skiff on davits outboard of the port quarter: the ship's boat for landing where there is no mooring (a small
  // lift stone in its keel).
  {
    const x0 = 30.4;
    const x1 = 35.6;
    const zc = -(halfBreadth(33) + 1.35);
    const yc = 0.95;
    const hull = new THREE.CylinderGeometry(0.78, 0.78, x1 - x0, 10, 1, false, Math.PI * 0.5, Math.PI);
    hull.rotateZ(Math.PI / 2);
    hull.scale(1, 0.75, 1);
    primitive(root, mat('hullWood'), hull, (x0 + x1) / 2, yc, zc);
    B('rail', (x0 + x1) / 2, yc + 0.02, zc, x1 - x0, 0.06, 1.6);
    for (const x of [x0 + 1.1, (x0 + x1) / 2, x1 - 1.1]) B('spar', x, yc - 0.1, zc, 0.12, 0.12, 1.5);
    B('bulwark', (x0 + x1) / 2, yc + 0.08, zc, x1 - x0 - 0.8, 0.06, 1.3);
    primitive(root, mat('liftStone'), new THREE.OctahedronGeometry(0.16, 0).scale(1, 1.4, 1), (x0 + x1) / 2, yc - 0.62, zc);
    for (const x of [x0 + 0.5, x1 - 0.5]) {
      const base: Vec3 = [x, sheer(x) + 0.2, -(hullZ(x, sheer(x)) - 0.05)];
      L('iron', [x, -0.4, -(hullZ(x, -0.4) + 0.04)], base, 0.07, 6);
      L('iron', base, [x, sheer(x) + 1.9, zc * 0.95 + -0.15], 0.07, 6);
      L('iron', [x, sheer(x) + 1.9, zc * 0.95 - 0.15], [x, sheer(x) + 1.75, zc], 0.06, 6);
      rope([x, sheer(x) + 1.7, zc], [x, yc + 0.1, zc], 0.025, null, 0.3);
    }
    // Gripes: canvas straps holding the boat against the davits.
    for (const x of [x0 + 1.6, x1 - 1.6]) rope([x, yc + 0.12, zc + 0.8], [x, sheer(x) + 0.3, -(hullZ(x, sheer(x)) - 0.1)], 0.03, null, 0.1);
  }

  // --- Rail lanterns on the stanchions (hung inboard on iron brackets).
  for (const side of [1, -1]) {
    for (const x of [31.6, 39.4, 47.2]) {
      const z = side * (hullZ(x, sheer(x)) - 0.07);
      const cap = sheer(x) + 0.62;
      B('iron', x, cap + 0.05, z - side * 0.16, 0.05, 0.05, 0.36);
      lantern(root, kit, [x, cap - 0.02, z - side * 0.32]);
    }
  }

  // --- Gangplank furniture: grip battens, stanchions and handropes. It goes with the jetty when the ship casts off.
  {
    const gp = area.surfaces.find((s) => s.id === 'gangplank');
    if (gp !== undefined) {
      for (let x = gp.x0 + 0.25; x < gp.x1 - 0.1; x += 0.42) B('rail', x, 0.018, (gp.z0 + gp.z1) / 2, 0.07, 0.036, gp.z1 - gp.z0 - 0.16, 'mooring');
      // One handrope, on the far (north) side: the near side stays open so Brad reads clearly boarding and returning
      // (the gotcha: gangplank sightlines).
      for (const z of [gp.z0 + 0.06]) {
        const posts = [gp.x0 + 0.3, (gp.x0 + gp.x1) / 2, gp.x1 - 0.35];
        for (const x of posts) {
          B('iron', x, 0.5, z, 0.05, 1, 0.05, 'mooring');
          withPart(primitive(root, mat('iron'), new THREE.TorusGeometry(0.045, 0.012, 3, 6).rotateY(Math.PI / 2), x, 0.98, z), 'mooring');
        }
        for (let k = 0; k < posts.length - 1; k += 1) ropePath(catenary([at(posts, k), 0.98, z], [at(posts, k + 1), 0.98, z], 0.12, 6), 0.02, 'mooring', 0.5);
        ropePath(catenary([last(posts), 0.98, z], [stern + 0.12, 1.3, z < 0 ? -1.5 : 1.5], 0.08, 4), 0.02, 'mooring', 0.3);
      }
    }
  }
  // --- Mooring lines from the quarter bitts to the jetty bollards.
  for (const [s, bz] of [
    [-1, -4.35],
    [1, 5.35],
  ] as const) {
    const bitt: Vec3 = [stern + 1.1, 0.0, s * 7.35];
    B('iron', bitt[0], 0.28, bitt[2] - 0.18, 0.16, 0.56, 0.16);
    B('iron', bitt[0], 0.28, bitt[2] + 0.18, 0.16, 0.56, 0.16);
    B('iron', bitt[0], 0.42, bitt[2], 0.1, 0.1, 0.5);
    B('iron', 24.9, 0.25, bz, 0.22, 0.5, 0.22, 'mooring');
    B('iron', 24.9, 0.52, bz, 0.3, 0.06, 0.3, 'mooring');
    ropePath(catenary([bitt[0], 0.46, bitt[2]], [24.9, 0.4, bz], 0.55, 10), 0.03, 'mooring', 0.6);
  }
};

/** The exterior of a ship's deck scene: the mooring island and jetty (part `mooring`, left behind when the ship casts off) and the ship. */
export const buildExterior = (kit: ShipKit, area: ShipArea): THREE.Group => {
  exteriorMaterials(kit);
  const root = new THREE.Group();
  root.name = 'Airship exterior';
  const mooringRoot = new THREE.Group();
  root.add(mooringRoot);
  buildMooring(mooringRoot, kit, area);
  mooringRoot.traverse((o) => {
    if (o instanceof THREE.Mesh) tagMesh(o, { part: 'mooring' });
  });
  buildShip(root, kit, area);
  return root;
};
